import PageBuilder from '@/components/pagebuilder/pageBuilder';
import Products from '@/components/products';
import Container from '@/components/ui/container';
import Divider from '@/components/ui/divider';
import { PRODUCT_LIST_ROWS } from '@/contexts/product/list';
import { useStoreEmails } from '@/hooks/store/useStoreEmails';
import { withStaticProps } from '@/server/app';
import { gaClient } from '@/utils/ga';
import { decodeURIPart, parseProductsOptionsRouteParam, ProductsOptions } from '@/utils/routes';
import { basePath } from '@/utils/stores';
import { trpc } from '@/utils/trpc';
import { unbxdClient } from '@/utils/unbxd';
import { UnbxdFacets, UnbxdResponse } from '@/utils/unbxd/types';
import { GetStaticPaths } from 'next';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import Image from 'next/image';
import { useEffect } from 'react';

interface SearchPageProps {
  query: string;
  requestId: string;
  response: UnbxdResponse;
  facets?: UnbxdFacets;
  options: ProductsOptions;
  isEmpty: boolean;
}

function EmptyPage(props: SearchPageProps) {
  const { data } = trpc.cmsBlocks.get.useQuery({
    identifiers: ['unbxd-recs-empty-search'],
  });
  const { t } = useTranslation('common');
  const customerSupportEmail = useStoreEmails('customer_support_email').sender_email;

  return (
    <>
      <div className="flex w-full items-center justify-center pb-8 pt-8 align-middle">
        <Image
          src={basePath + '/assets/images/sad_face.svg'}
          alt={t('error-heading')}
          width={60}
          height={60}
        />
      </div>
      <div className="flex w-full items-center justify-center align-middle text-xl font-bold md:text-2xl">
        <span>{t('text-search-empty', { title: props.query })}</span>
      </div>
      <div className="flex w-full items-center justify-center align-middle">
        <div className="pt-8">
          <p className="text-xl font-bold">Tips:</p>
          <ul className="list-disc space-y-3.5 p-3">
            <li>{t('text-empty-search-1')}</li>
            <li>{t('text-empty-search-2')}</li>
            <li>{t('text-empty-search-3')}</li>
            <li>
              {t('text-empty-search-4')} <a href={`mailto:${customerSupportEmail}`}>{customerSupportEmail}</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="user-defined-cms-block unbxd-recs-empty-search w-full px-0 py-16">
        {data?.[0] && (
          <PageBuilder
            pagebuilder={data[0]?.pagebuilder}
            noWrapper={true}
          />
        )}
      </div>
    </>
  );
}

export default function SearchPage(props: SearchPageProps) {
  useEffect(() => {
    gaClient.pushEvent({
      event: 'search',
      search_term: props.query,
    });
  }, [props.query]);

  return (
    <>
      <Head>
        <title>{'Search: ' + props.query}</title>
      </Head>
      <Divider className="mb-0" />
      <Container>
        {props.isEmpty && <EmptyPage {...props} />}
        {!props.isEmpty && (
          <Products
            type="search"
            title={props.query}
            urlKey={props.query}
            fetchKey={props.query}
            breadcrumbs={[
              {
                name: 'Search',
              },
            ]}
            requestId={props.requestId}
            response={props.response}
            facets={props.facets}
            options={props.options}
          />
        )}
      </Container>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = withStaticProps<SearchPageProps>(async ({ params }) => {
  const query = decodeURIPart((params?.args?.[0] as string) ?? '??');

  if (query) {
    const options = parseProductsOptionsRouteParam(params?.args?.[1]);

    let facets: UnbxdFacets | undefined;

    const {
      requestId,
      response,
      redirect,
      facets: facets1,
      searchMetaData,
    } = await unbxdClient.search({
      query: query,
      rows: PRODUCT_LIST_ROWS,
      ...options,
    });

    facets = facets1;

    if (options.filters) {
      facets = (
        await unbxdClient.search({
          query: query,
          rows: PRODUCT_LIST_ROWS,
          ...options,
          filters: undefined,
        })
      ).facets;
    }

    if (redirect && redirect.type == 'url') {
      const urlObj = new URL(redirect.value);
      const searchParams = urlObj.searchParams;
      searchParams.set('redirectFromSearch', query);
      urlObj.search = urlObj.searchParams.toString();

      return {
        redirect: {
          permanent: false,
          destination: urlObj.href,
        },
      };
    }

    const isEmpty = !options.filters && response.numberOfProducts == 0;

    return {
      props: {
        query: searchMetaData.fallback?.q ?? query,
        isEmpty,
        requestId,
        response,
        ...(facets && { facets }),
        options,
      },
    };
  }

  return {
    notFound: true,
  };
});
